/**
 * Quixo staff access requests (flag identity.staff_access_approval; docs/runbooks/staff-access-approval.md).
 * Agency owners and admins approve or decline Quixo staff access to the agency itself or to a client workspace the
 * agency manages, and can revoke an active grant any time. Partner members get 403 from the API: the screen then says
 * only owners and admins decide. While the switch is off the API answers enabled:false and the screen says so.
 */
import { useState } from 'react';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { errorInfo } from '../kit/http';
import { useAction, useResource } from '../kit/resource';
import { Badge, EmptyState, ErrorNote, Field, Notice, Screen, When, type Tone } from '../kit/ui';
import type { StaffAccessItem, StaffAccessList } from '../api';
import { usePartnerApi } from './layout';

const TONE: Record<string, Tone> = { pending: 'warn', active: 'good', declined: 'bad', revoked: 'bad', expired: 'neutral', lapsed: 'neutral', cancelled: 'neutral' };

export function StaffAccessPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const res = useResource('identity:staff-access', api.staffAccessRequests);
  return (
    <Screen title={t('portal.partners.staffAccess.title')} subtitle={t('portal.partners.staffAccess.intro')} testId="partner-staff-access">
      {res.status === 'loading' ? <Text tone="muted">{t('portal.kit.loading')}</Text> : null}
      {res.status === 'error' ? (errorInfo(res.error).status === 403
        ? <Notice testId="staff-access-not-owner">{t('portal.partners.staffAccess.ownersOnly')}</Notice>
        : <ErrorNote error={res.error} />) : null}
      {res.status === 'ready' ? <StaffAccessList data={res.data} onChanged={res.reload} /> : null}
    </Screen>
  );
}

export function StaffAccessList({ data, onChanged }: { data: StaffAccessList; onChanged: () => void }) {
  const { t } = useSurface();
  if (!data.enabled) return <Notice testId="staff-access-off">{t('portal.partners.staffAccess.off')}</Notice>;
  if (!data.items.length) return <EmptyState message={t('portal.partners.staffAccess.none')} />;
  return <>{data.items.map((i) => <StaffAccessCard key={i.id} item={i} onChanged={onChanged} />)}</>;
}

export function StaffAccessCard({ item, onChanged }: { item: StaffAccessItem; onChanged: () => void }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [note, setNote] = useState('');
  const act = useAction(async (verb: 'approve' | 'decline' | 'revoke') => { const r = await api.decideStaffAccess(item.id, verb, note); onChanged(); return r; });
  const who = item.staff.name ? `${item.staff.name} (${item.staff.email})` : item.staff.email;
  const target = item.target.type === 'partner' ? t('portal.partners.staffAccess.forAgency') : t('portal.partners.staffAccess.forClient', { name: item.target.name ?? item.target.id });
  const open = item.status === 'pending' || item.status === 'active';
  return (
    <Panel data-testid={`staff-access-${item.id}`}>
      <Heading level={2}>{t('portal.partners.staffAccess.asked', { who })}</Heading>
      <Badge tone={TONE[item.status] ?? 'neutral'}>{t(`portal.partners.staffAccess.status.${item.status}`)}</Badge>
      <Text>{target}</Text>
      <Text>{t('portal.partners.staffAccess.level')}: {t(`portal.partners.staffAccess.scope.${item.scope}`)}</Text>
      <Text>{t('portal.partners.staffAccess.reason')}: <bdi>{item.reason}</bdi></Text>
      <Text tone="muted">{t('portal.partners.staffAccess.duration', { hours: item.durationHours })}</Text>
      {item.status === 'active' && item.expiresAt ? <Text tone="muted">{t('portal.partners.staffAccess.endsAt')} <When iso={item.expiresAt} /></Text> : null}
      {open ? (
        <>
          <Field label={t('portal.partners.staffAccess.note')}>
            {(id) => <input id={id} name="note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />}
          </Field>
          {item.status === 'pending' ? (
            <>
              <Button variant="primary" onClick={() => void act.run('approve')} disabled={act.pending} data-testid={`staff-access-approve-${item.id}`}>{t('portal.partners.staffAccess.approve')}</Button>
              <Button onClick={() => void act.run('decline')} disabled={act.pending} data-testid={`staff-access-decline-${item.id}`}>{t('portal.partners.staffAccess.decline')}</Button>
            </>
          ) : (
            <Button onClick={() => void act.run('revoke')} disabled={act.pending} data-testid={`staff-access-revoke-${item.id}`}>{t('portal.partners.staffAccess.revoke')}</Button>
          )}
        </>
      ) : null}
      <ErrorNote error={act.error} />
    </Panel>
  );
}
